/**
 * Copyright © 2024-2026 Entangled Harmony Labs, LLC
 * All Rights Reserved. Proprietary and Confidential.
 */
import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { 
  ChevronRight, 
  ChevronLeft, 
  X, 
  Guitar, 
  Music, 
  Eye, 
  Zap,
  Target,
  PlayCircle
} from 'lucide-react';

interface OnboardingStep {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  target?: string; // CSS selector for highlighting
  position: 'top' | 'bottom' | 'left' | 'right' | 'center';
  action?: string; // What the user should do
}

const onboardingSteps: OnboardingStep[] = [
  {
    id: 'welcome',
    title: 'Welcome to Guituitive!',
    description: 'A comprehensive music theory tool designed for guitarists who want to visualize scales, chords, and theory on the fretboard.',
    icon: <Guitar className="h-6 w-6" />,
    position: 'center'
  },
  {
    id: 'fretboard',
    title: 'Visual Fretboard',
    description: 'This is your main workspace where you can view scales, chord fingerings, and interval relationships. The fretboard adapts to different instruments, tunings and orientations, displaying your selected music theory concepts.',
    icon: <Target className="h-6 w-6" />,
    target: '.fretboard-container',
    position: 'top'
  },
  {
    id: 'controls',
    title: 'Mode',
    description: 'Access mode switching between Scale Mode, Chord Mode, and Practice Mode here. You can also select your key and choose from different scales or chords. Check out Practice mode for ear training and fretboard exercises!',
    icon: <Music className="h-6 w-6" />,
    target: '.mode-selector',
    position: 'left'
  },
  {
    id: 'tuning',
    title: 'Instrument Setup',
    description: 'Here you can customize your instrument setup. Change tunings, adjust string count (4-8 strings), set fret range, and toggle between horizontal/vertical layouts.',
    icon: <Guitar className="h-6 w-6" />,
    target: '.instrument-config',
    position: 'left'
  },
  {
    id: 'insights',
    title: 'Harmonic Companion',
    description: 'Get intelligent explanations about scales, chord progressions, and music theory concepts. Perfect for understanding the "why" behind what you\'re playing.',
    icon: <Zap className="h-6 w-6" />,
    target: '.harmonic-companion-button',
    position: 'right'
  },
  {
    id: 'visualization',
    title: 'Smart Visualization',
    description: 'Color-coded intervals help you see patterns. Toggle note names, adjust what\'s highlighted, and use the eye icon to show/hide different elements.',
    icon: <Eye className="h-6 w-6" />,
    target: '.intervals-legend',
    position: 'bottom'
  },
  {
    id: 'jamtracks',
    title: 'Jam Tracks (Pro Feature)',
    description: 'Play along with backing tracks while seeing chord changes on the fretboard. Perfect for practicing scales over real chord progressions.',
    icon: <PlayCircle className="h-6 w-6" />,
    target: '.backing-track-panel',
    position: 'left'
  }
];

interface OnboardingGuideProps {
  isVisible: boolean;
  onComplete: () => void;
  onSkip: () => void;
}

const OnboardingGuide: React.FC<OnboardingGuideProps> = ({
  isVisible,
  onComplete,
  onSkip
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isMinimized, setIsMinimized] = useState(false);

  useEffect(() => {
    if (isVisible && onboardingSteps[currentStep]?.target) {
      const element = document.querySelector(onboardingSteps[currentStep].target!);
      if (element) {
        // Don't highlight or scroll the chat button - it's already visually prominent and fixed
        if (onboardingSteps[currentStep].target !== '.harmonic-companion-button') {
          element.classList.add('onboarding-highlight');
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    }

    return () => {
      // Clean up highlights
      document.querySelectorAll('.onboarding-highlight').forEach(el => {
        el.classList.remove('onboarding-highlight');
      });
    };
  }, [currentStep, isVisible]);

  if (!isVisible) return null;

  const currentStepData = onboardingSteps[currentStep];
  const isLastStep = currentStep === onboardingSteps.length - 1;
  const isFirstStep = currentStep === 0;

  const handleNext = () => {
    if (isLastStep) {
      onComplete();
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrev = () => {
    if (!isFirstStep) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSkip = () => {
    onSkip();
  };

  if (isMinimized) {
    return (
      <div className="fixed bottom-4 right-4 z-50">
        <Button 
          onClick={() => setIsMinimized(false)}
          className="bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg"
        >
          <Guitar className="h-4 w-4 mr-2" />
          Continue Guide ({currentStep + 1}/{onboardingSteps.length})
        </Button>
      </div>
    );
  }

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/20 z-40" />
      
      {/* Guide Card - Fixed position near helpful hints icon */}
      <div className="fixed top-4 right-96 z-50 transition-all duration-300">
        <Card className="w-80 shadow-xl border-2 border-primary/20">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary/10 text-primary">
                  {currentStepData.icon}
                </div>
                <CardTitle className="text-lg">{currentStepData.title}</CardTitle>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsMinimized(true)}
                  className="h-8 w-8 p-0"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleSkip}
                  className="h-8 w-8 p-0"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardHeader>
          
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground leading-relaxed">
              {currentStepData.description}
            </p>
            
            {currentStepData.action && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800 font-medium">
                  💡 Try this: {currentStepData.action}
                </p>
              </div>
            )}
            
            <div className="flex items-center justify-between pt-3">
              <div className="flex items-center gap-2">
                <Badge variant="outline">
                  {currentStep + 1} of {onboardingSteps.length}
                </Badge>
                <div className="flex gap-1">
                  {onboardingSteps.map((_, index) => (
                    <div
                      key={index}
                      className={`w-2 h-2 rounded-full transition-colors ${
                        index <= currentStep ? 'bg-primary' : 'bg-muted'
                      }`}
                    />
                  ))}
                </div>
              </div>
              
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handlePrev}
                  disabled={isFirstStep}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Back
                </Button>
                <Button
                  size="sm"
                  onClick={handleNext}
                  className="bg-primary hover:bg-primary/90"
                >
                  {isLastStep ? 'Get Started' : 'Next'}
                  {!isLastStep && <ChevronRight className="h-4 w-4 ml-1" />}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
};

export default OnboardingGuide;